import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useConfig } from "../../../hooks/use-config";
import { useToast } from "../../../hooks/use-toast";
import { useTranslation } from "react-i18next";
import { createProject, CreateProjectRequest, renameProject, renameProjectRequest, updateProject, UpdateProjectRequest } from "../services/projects-services";

interface CreateProjectVariables {
    payload: CreateProjectRequest;
    skipInvalidate?: boolean
}

interface UpdateProjectVariables {
    payload: UpdateProjectRequest;
    skipInvalidate?: boolean
}
interface renameProjectVariables {
    payload: renameProjectRequest;
    skipInvalidate?: boolean
}

export const useCreateProject = () => {
    const { configurationSettings } = useConfig();
    const { toast } = useToast();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: ({ payload }: CreateProjectVariables) =>
            createProject(configurationSettings, payload),
        onSuccess: () => {
            toast({
                variant: 'success',
                title: t('SUCCESS'),
                description: t('PROJECT_CREATED_SUCCESSFULLY'),
            });
        },
        onError: (error) => {
            toast({
                variant: 'destructive',
                title: t('FAILED'),
                description: error?.message ?? t('PROJECT_CREATION_FAILED'),
            });
        },
    });
};


export const useUpdateProject = () => {
    const { configurationSettings } = useConfig();
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: ({ payload }: UpdateProjectVariables) =>
            updateProject(configurationSettings, payload),
        onSuccess: (_data, variables) => {
            const skipInvalidate = variables?.skipInvalidate ?? false;
            if (!skipInvalidate) {
                queryClient.invalidateQueries({ queryKey: ['getProjects'] });
            }
        },
        onError: (error) => {
            toast({
                variant: 'destructive',
                title: t('FAILED'),
                description: error?.message ?? t('PROJECT_CREATION_FAILED'),
            });
        },
    });
};

export const useRenameProject = () => {
    const { configurationSettings } = useConfig();
    const { toast } = useToast();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: ({ payload }: renameProjectVariables) =>
            renameProject(configurationSettings, payload),
        onSuccess: () => {
            toast({
                variant: 'success',
                title: t('SUCCESS'),
                description: t('PROJECT_RENAMED_SUCCESSFULLY'),
            });
        },
        onError: (error) => {
            toast({
                variant: 'destructive',
                title: t('FAILED'),
                description: error?.message ?? t('PROJECT_RENAMING_FAILED'),
            });
        },
    });
};
